import { GlobalDialogType } from '@/stores/global-dialogs/global-dialogs.store.types';
import { useIsGlobalDialogOpen } from '@/stores/global-dialogs/hooks/useIsGlobalDialogOpen';
import { BoundariesRef } from '@recruit-robin/robin-theme/web-components';
import { useMemo, useRef } from 'react';

const REACHOUT_INTERVIEW_MODAL_ID = 'reachout-interview-modal';
const OUTREACH_MODAL_ID = 'outreach-modal-root';

export const useBoundaries = () => {
  const isReachoutInterviewModalOpen = useIsGlobalDialogOpen(GlobalDialogType.ReachoutInterviewModal);
  const isOutreachModalOpen = useIsGlobalDialogOpen(GlobalDialogType.CandidateOutreach);
  const boundariesRef = useRef<BoundariesRef>(null);

  useBoundariesByElementId(REACHOUT_INTERVIEW_MODAL_ID, isReachoutInterviewModalOpen, boundariesRef);
  useBoundariesByElementId(OUTREACH_MODAL_ID, isOutreachModalOpen, boundariesRef);

  return boundariesRef;
};

const useBoundariesByElementId = (
  elementId: string,
  isVisible: boolean,
  boundariesRef: React.RefObject<BoundariesRef>,
) => {
  useMemo(() => {
    if (isVisible) {
      const observer = new ResizeObserver((entries) => {
        const { width } = entries[0].contentRect;

        const constraints = {
          height: window.innerHeight,
          width: innerWidth - width,
        };

        boundariesRef.current?.setConstraints(constraints);
        boundariesRef.current?.recalculate();
      });

      getDialog(elementId).then((dialog) => {
        observer.observe(dialog);
      });

      return () => {
        observer.disconnect();
        boundariesRef.current?.setConstraints(null);
      };
    }
  }, [isVisible]);
};

const getDialog = (dialogId: string) => {
  return new Promise<HTMLElement>((resolve) => {
    const getDialogFromNextFrame = () => {
      requestAnimationFrame(() => {
        const dialog = document.getElementById(dialogId);

        if (dialog) {
          resolve(dialog);
        } else {
          getDialogFromNextFrame();
        }
      });
    };

    getDialogFromNextFrame();
  });
};
